import Cookies from "js-cookie";
import {
  ADMIN_OVERALL_TEST_CREATE_URL,
  ADMIN_OVERALL_TEST_UPDATE_URL,
  ADMIN_OVERALL_TEST_DELETE_URL,
  ADMIN_OVERALL_TEST_GET_URL,
  ADMIN_OVERALL_TEST_GET_SINGLE_URL,
  ADMIN_GET_OVERALL_TEST_SUBMITTED_STUDENTS,
  ADMIN_GET_OVERALL_TEST_SUBMITTED_STUDENT,
  OVERALL_TEST_CREATE_URL,
  OVERALL_TEST_UPDATE_URL,
  OVERALL_TEST_DELETE_URL,
  OVERALL_TEST_GET_URL,
  OVERALL_TEST_GET_SINGLE_URL,
  GET_OVERALL_TEST_SUBMITTED_STUDENTS,
  GET_OVERALL_TEST_SUBMITTED_STUDENT,
  TEACHER_OVERALL_TEST_GET_URL,
  TEACHER_OVERALL_TEST_GET_SINGLE_URL,
  TEACHER_OVERALL_TEST_CREATE_URL,
  TEACHER_OVERALL_TEST_UPDATE_URL,
  TEACHER_OVERALL_TEST_DELETE_URL,
  TEACHER_GET_OVERALL_TEST_SUBMITTED_STUDENTS,
  TEACHER_GET_OVERALL_TEST_SUBMITTED_STUDENT,
} from "../apiurls/apiurls";

const isAdmin = () => Cookies.get("role") === "admin";
const isTeacher = () => Cookies.get("role") === "teacher";

const getAuthToken = () =>
  isAdmin() ? Cookies.get("admin_token") : Cookies.get("institution_token");

const getHeaders = () => {
  const headers = { "Content-Type": "application/json" };
  const token = getAuthToken();
  if (token) {
    headers["Authorization"] = ` ${token}`;
  }
  return headers;
};

export const getOverallTests = async (courseKey) => {
  try {
    const baseGetUrl = isAdmin()
      ? ADMIN_OVERALL_TEST_GET_URL
      : isTeacher
        ? TEACHER_OVERALL_TEST_GET_URL
        : OVERALL_TEST_GET_URL;
    let url = baseGetUrl;

    if (typeof courseKey === "string" && courseKey.trim()) {
      const params = new URLSearchParams({ course: courseKey.trim() });
      url = `${baseGetUrl}?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Error fetching overall tests:", error);
    throw error;
  }
};

export const getOverallTestById = async (id) => {
  if (!id) throw new Error("Overall test id is required");

  try {
    const baseGetSingleUrl = isAdmin()
      ? ADMIN_OVERALL_TEST_GET_SINGLE_URL
      : isTeacher()
        ? TEACHER_OVERALL_TEST_GET_SINGLE_URL
        : OVERALL_TEST_GET_SINGLE_URL;
    const url = `${baseGetSingleUrl}?id=${encodeURIComponent(id)}`;
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching overall test by id:", error);
    throw error;
  }
};

export const createOverallTest = async (payload) => {
  try {
    const createUrl = isAdmin()
      ? ADMIN_OVERALL_TEST_CREATE_URL
      : isTeacher()
        ? TEACHER_OVERALL_TEST_CREATE_URL
        : OVERALL_TEST_CREATE_URL;
    const response = await fetch(createUrl, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating overall test:", error);
    throw error;
  }
};

export const updateOverallTest = async (id, payload) => {
  if (!id) throw new Error("Overall test id is required to update");

  try {
    const baseUpdateUrl = isAdmin()
      ? ADMIN_OVERALL_TEST_UPDATE_URL
      : isTeacher()
        ? TEACHER_OVERALL_TEST_UPDATE_URL
        : OVERALL_TEST_UPDATE_URL;
    const url = `${baseUpdateUrl}?id=${encodeURIComponent(id)}`;
    const response = await fetch(url, {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating overall test:", error);
    throw error;
  }
};

export const deleteOverallTest = async (id) => {
  if (!id) throw new Error("Overall test id is required to delete");

  try {
    const baseDeleteUrl = isAdmin()
      ? ADMIN_OVERALL_TEST_DELETE_URL
      : isTeacher()
        ? TEACHER_OVERALL_TEST_DELETE_URL
        : OVERALL_TEST_DELETE_URL;
    const url = `${baseDeleteUrl}?id=${encodeURIComponent(id)}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting overall test:", error);
    throw error;
  }
};

export const getOverallTestSubmittedStudents = async (overallTestId) => {
  if (!overallTestId) {
    throw new Error("Overall test id is required to fetch submitted students");
  }

  try {
    const baseSubmittedUrl = isAdmin()
      ? ADMIN_GET_OVERALL_TEST_SUBMITTED_STUDENTS
      : isTeacher()
        ? TEACHER_GET_OVERALL_TEST_SUBMITTED_STUDENTS
        : GET_OVERALL_TEST_SUBMITTED_STUDENTS;
    const url = `${baseSubmittedUrl}?test_id=${encodeURIComponent(
      overallTestId,
    )}`;
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Error fetching submitted students for overall test:", error);
    throw error;
  }
};

export const getOverallTestSubmittedStudent = async (submissionId) => {
  if (!submissionId) {
    throw new Error("Submission id is required to fetch submitted student");
  }

  try {
    const baseSubmittedSingleUrl = isAdmin()
      ? ADMIN_GET_OVERALL_TEST_SUBMITTED_STUDENT
      : isTeacher()
        ? TEACHER_GET_OVERALL_TEST_SUBMITTED_STUDENT
        : GET_OVERALL_TEST_SUBMITTED_STUDENT;
    const url = `${baseSubmittedSingleUrl}?id=${encodeURIComponent(
      submissionId,
    )}`;
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching submitted student for overall test:", error);
    throw error;
  }
};
